import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPaw,
  faHome,
  faClock,
  faHeart,
  faCheckCircle,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import { getQuizSummary } from '../utils/quizStorage';

/**
 * Component to display saved quiz responses
 * Can be used throughout the app to show user preferences
 */
function QuizResponsesDisplay({ showHeader = true, compact = false }) {
  const quizSummary = getQuizSummary();
  
  if (!quizSummary.isCompleted) {
    return null;
  }

  const questions = [
    {
      id: 'experience',
      title: 'Pet Experience',
      icon: faHeart,
      options: {
        'first-time': 'First-time pet owner',
        'some': 'Some experience with pets',
        'experienced': 'Very experienced with pets',
      },
    },
    {
      id: 'living',
      title: 'Living Situation',
      icon: faHome,
      options: {
        'apartment': 'Apartment/Condo',
        'house-small': 'House with small yard',
        'house-large': 'House with large yard',
        'farm': 'Farm/Rural property',
      },
    },
    {
      id: 'time',
      title: 'Daily Time Commitment',
      icon: faClock,
      options: {
        'minimal': '1-2 hours',
        'moderate': '3-4 hours',
        'lots': '5+ hours',
        'all-day': 'Most of the day',
      },
    },
    {
      id: 'size',
      title: 'Pet Size Preference',
      icon: faPaw,
      options: {
        'small': 'Small (under 25 lbs)',
        'medium': 'Medium (25-60 lbs)',
        'large': 'Large (60+ lbs)',
        'any': 'No preference',
      },
    },
    {
      id: 'energy',
      title: 'Energy Level Preference',
      icon: faCheckCircle,
      options: {
        'low': 'Low energy - likes to relax',
        'moderate': 'Moderate - some playtime',
        'high': 'High energy - loves to play',
        'very-high': 'Very high - needs lots of exercise',
      },
    },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const containerClasses = compact 
    ? 'bg-white dark:bg-neutral-800 rounded-lg p-4'
    : 'bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6';

  const gridClasses = compact
    ? 'grid grid-cols-1 sm:grid-cols-2 gap-3'
    : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4';

  return (
    <div className={containerClasses}>
      {showHeader && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
            Your Pet Preferences
          </h2>
          {quizSummary.lastCompleted && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 flex items-center">
              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
              Quiz completed on {formatDate(quizSummary.lastCompleted)}
            </p>
          )}
        </div>
      )}
      
      <div className={gridClasses}>
        {questions.map((question) => {
          const answer = quizSummary.responses[question.id];
          const answerLabel = question.options[answer] || 'No answer provided';
          
          return (
            <div 
              key={question.id} 
              className="bg-primary-50 dark:bg-primary-900 rounded-lg p-4"
            >
              <div className="flex items-center mb-2">
                <FontAwesomeIcon 
                  icon={question.icon} 
                  className="h-4 w-4 text-primary-600 dark:text-primary-400 mr-2"
                />
                <h3 className="font-medium text-primary-900 dark:text-primary-100 text-sm">
                  {question.title}
                </h3>
              </div>
              <p className="text-primary-700 dark:text-primary-200 text-sm">
                {answerLabel}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

QuizResponsesDisplay.propTypes = {
  showHeader: PropTypes.bool,
  compact: PropTypes.bool,
};

export default QuizResponsesDisplay;